import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MAP_SHEET_MEDIA = "(max-width: 767px), (min-width: 768px) and (max-width: 1279px) and (orientation: portrait)";

const LABELS = {
  es: { close: "Cerrar resultados", expand: "Ampliar resultados", collapse: "Reducir resultados" },
  en: { close: "Close results", expand: "Expand results", collapse: "Collapse results" },
  fr: { close: "Fermer les résultats", expand: "Agrandir les résultats", collapse: "Réduire les résultats" },
  it: { close: "Chiudi risultati", expand: "Espandi risultati", collapse: "Riduci risultati" },
  de: { close: "Ergebnisse schließen", expand: "Ergebnisse erweitern", collapse: "Ergebnisse verkleinern" },
  pt: { close: "Fechar resultados", expand: "Expandir resultados", collapse: "Reduzir resultados" },
};

/** Non-modal: destination points and chips remain usable above the sheet. */
export function HomeMapResults({ open, onClose, title, lang, children }: {
  open: boolean; onClose: () => void; title: string; lang: keyof typeof LABELS; children: ReactNode;
}) {
  const [compact, setCompact] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const surface = useRef<HTMLDivElement>(null);
  const handle = useRef<HTMLButtonElement>(null);
  const origin = useRef<HTMLElement | null>(null);
  const ownsHistory = useRef(false);
  const dragStart = useRef<number | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const L = LABELS[lang];

  useEffect(() => {
    const mq = window.matchMedia(MAP_SHEET_MEDIA);
    const sync = () => setCompact(mq.matches);
    sync(); mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!compact || !open) return;
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setExpanded(false);
    window.history.pushState({ ...window.history.state, mmMapResults: true }, "", window.location.href);
    ownsHistory.current = true;
    const frame = requestAnimationFrame(() => handle.current?.focus({ preventScroll: true }));
    const back = () => { ownsHistory.current = false; closeRef.current(); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); closeRef.current(); }
    };
    window.addEventListener("popstate", back);
    window.addEventListener("keydown", key);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("popstate", back);
      window.removeEventListener("keydown", key);
      if (ownsHistory.current && window.history.state?.mmMapResults) {
        ownsHistory.current = false;
        window.history.back();
      }
      if (origin.current?.isConnected) origin.current.focus({ preventScroll: true });
    };
  }, [compact, open]);

  // A result link must navigate normally, without a stale sheet history entry.
  const leave = (e: React.MouseEvent) => {
    if (!(e.target instanceof Element) || !e.target.closest("a[href]")) return;
    ownsHistory.current = false;
    const { mmMapResults: _sheet, ...state } = window.history.state ?? {};
    window.history.replaceState(state, "", window.location.href);
    closeRef.current();
  };

  if (!compact) return <div className="home-map-results min-w-0">{children}</div>;
  if (!open) return null;
  return createPortal(
    <div ref={surface} role="dialog" aria-modal="false" aria-label={title} data-map-results-sheet data-expanded={expanded}
      className="home-map-results home-map-results-sheet bg-background text-foreground shadow-elevated" onClickCapture={leave}>
      <div className="relative flex shrink-0 items-center justify-center border-b border-border px-14">
        <Button ref={handle} variant="ghost" aria-label={expanded ? L.collapse : L.expand} aria-expanded={expanded}
          className="h-11 w-full touch-none hover:bg-muted"
          onClick={() => setExpanded(v => !v)}
          onKeyDown={e => { if (e.key === "ArrowUp") { e.preventDefault(); setExpanded(true); } if (e.key === "ArrowDown") { e.preventDefault(); if (expanded) setExpanded(false); else onClose(); } }}
          onPointerDown={e => { dragStart.current = e.clientY; e.currentTarget.setPointerCapture(e.pointerId); }}
          onPointerUp={e => {
            if (dragStart.current === null) return;
            const delta = e.clientY - dragStart.current; dragStart.current = null;
            if (Math.abs(delta) < 35) return;
            e.preventDefault();
            if (delta < 0) setExpanded(true); else onClose();
          }}>
          <span className="h-1 w-12 rounded-full bg-muted-foreground/40" aria-hidden />
          {expanded ? <ChevronDown className="size-4 text-muted-foreground" aria-hidden /> : <ChevronUp className="size-4 text-muted-foreground" aria-hidden />}
        </Button>
        <Button variant="ghost" size="icon" aria-label={L.close} onClick={onClose} className="absolute right-2 top-0.5"><X /></Button>
      </div>
      <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-4" onKeyDown={e => { if (e.key === "Escape") onClose(); }}>{children}</div>
    </div>, document.body,
  );
}